import { createFileRoute, redirect, isRedirect } from "@tanstack/react-router";
import { authClient } from "@/lib/auth-client";
import { getCachedSession } from "@/lib/offline-session";
import { useState } from "react";
import { resetThisDevice } from "@/lib/reset-device";
import { LoginForm } from "@/components/auth/login-form";
import { CommandSsoBlock } from "@/components/auth/command-sso-button";

// Phase 10: build-time flag. Off = today's login page byte-for-byte (no block
// mounted, no /api/sso/config request); on = "Sign in with Paris Command" above
// the untouched password form, gated again at runtime by /api/sso/config.
const SSO_ON = import.meta.env.VITE_COMMAND_SSO_ENABLED === "true";

export const Route = createFileRoute("/login")({
  beforeLoad: async () => {
    // If already authenticated online, redirect to home
    try {
      const { data: session } = await authClient.getSession();
      if (session) {
        throw redirect({ to: "/" });
      }
    } catch (err) {
      // If this is a redirect (already authenticated), re-throw it
      if (isRedirect(err)) throw err;
      // Network error -- check for cached session to skip login
      const cached = await getCachedSession();
      if (cached) {
        throw redirect({ to: "/" });
      }
    }
  },
  // Phase 10: all keys OPTIONAL so existing <Link to="/login"> / navigate calls
  // need no `search`; `redirect` (set by _authenticated / _wizard) passes through.
  validateSearch: (
    s: Record<string, unknown>,
  ): { reset?: string; redirect?: string; sso?: "1"; error?: string } => ({
    ...(typeof s.reset === "string" ? { reset: s.reset } : {}),
    ...(typeof s.redirect === "string" ? { redirect: s.redirect } : {}),
    ...(s.sso === "1" || s.sso === 1 ? { sso: "1" as const } : {}),
    ...(typeof s.error === "string" && s.error ? { error: s.error } : {}),
  }),
  component: LoginPage,
});

function LoginPage() {
  const { reset, sso, error } = Route.useSearch();
  const [resetting, setResetting] = useState(false);

  function fixDevice() {
    setResetting(true);
    void resetThisDevice();
  }

  return (
    <div className="login-bg min-h-screen flex flex-col items-center justify-center px-6 py-12">
      {/* Logo */}
      <img
        src="/paris-safety-logo.png"
        alt="Paris Mechanical"
        className="w-56 md:w-64 mb-3 object-contain"
      />
      <p className="text-text-tertiary text-sm tracking-wider mb-10">
        Field Safety Management
      </p>

      {/* Glass card */}
      <div className="glass-card rounded-xl shadow-2xl w-full max-w-sm p-8">
        <div className="mb-6 text-center">
          <h1 className="text-xl font-semibold text-text-primary">Sign In</h1>
          <p className="text-sm text-text-secondary mt-1">
            Enter your username to continue
          </p>
        </div>
        {reset && (
          <p className="mb-4 rounded-md bg-muted px-3 py-2 text-center text-xs text-text-secondary">
            This device was reset. Please sign in again.
          </p>
        )}
        {SSO_ON ? <CommandSsoBlock autoStart={sso === "1"} error={error} /> : null}
        <LoginForm />
      </div>

      <button
        type="button"
        onClick={fixDevice}
        disabled={resetting}
        className="mt-6 text-xs text-text-muted underline underline-offset-2 disabled:opacity-60"
      >
        {resetting ? "Fixing this device…" : "Page not loading or can't sign in? Fix this device"}
      </button>

      {/* Copyright */}
      <p className="mt-10 text-[11px] text-text-muted">
        &copy; {new Date().getFullYear()} Paris Plumbing & Heating Ltd.
      </p>
    </div>
  );
}
